import { useState } from 'react'
import { Briefcase, Save } from 'lucide-react'
import { Card, CardTitle, CardDescription, Button, Input, Textarea, Label } from '@/components/ui'
import { usePatchApp } from '@/hooks/useApps'
import { formatCurrency, cn } from '@/lib/utils'
import type { AppRecord, AcquisitionStage } from '@/lib/types'
import { toast } from 'sonner'

interface AcquisitionDraft {
  stage: AcquisitionStage
  notes: string
  updated_at: string
  ask_price?: number
  offered_price?: number
  agreed_price?: number
  buyer_company?: string
  buyer_contact?: string
  buyer_email?: string
  negotiation_strategy?: string
}

const STAGES: { value: AcquisitionStage; label: string; tone: string }[] = [
  { value: 'not-listed',     label: 'Not Listed',     tone: 'text-cream/55' },
  { value: 'lead',           label: 'Lead',           tone: 'text-info' },
  { value: 'qualified',      label: 'Qualified',      tone: 'text-bronze' },
  { value: 'due-diligence',  label: 'Due Diligence',  tone: 'text-warning' },
  { value: 'loi',            label: 'LOI',            tone: 'text-gold-soft' },
  { value: 'closing',        label: 'Closing',        tone: 'text-gold' },
  { value: 'closed-won',     label: 'Closed Won',     tone: 'text-success' },
  { value: 'closed-lost',    label: 'Closed Lost',    tone: 'text-danger' },
]

export function AcquisitionPanel({ app }: { app: AppRecord }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<AcquisitionDraft>(app.acquisition as AcquisitionDraft ?? {
    stage: 'not-listed' as AcquisitionStage,
    notes: '',
    updated_at: new Date().toISOString(),
  })
  const patch = usePatchApp()

  async function save() {
    if (!app.id) return
    try {
      await patch.mutateAsync({
        id: app.id,
        patch: { acquisition: { ...draft, updated_at: new Date().toISOString() } },
      })
      toast.success('Acquisition info saved')
      setEditing(false)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <Card className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow mb-1">Acquisition</div>
          <CardTitle>Negotiation pipeline & strategy</CardTitle>
          <CardDescription className="mt-1">
            Stage-aware playbook for moving this app from prospect to closed transaction.
          </CardDescription>
        </div>
        {!editing ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="quiet" size="sm" onClick={() => { setEditing(false); setDraft(app.acquisition ?? draft) }}>Cancel</Button>
            <Button size="sm" loading={patch.isPending} onClick={save}><Save size={12} /> Save</Button>
          </div>
        )}
      </div>

      <div className="divider-editorial" />

      <div>
        <div className="eyebrow mb-3">Stage</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s) => (
            <button
              key={s.value}
              disabled={!editing}
              onClick={() => setDraft({ ...draft, stage: s.value })}
              className={cn(
                'px-3 py-1.5 rounded-md border font-mono text-[10px] uppercase tracking-[0.12em] transition-all',
                draft.stage === s.value
                  ? 'bg-gold/15 border-gold/40 text-gold-soft'
                  : 'bg-navy/40 border-cream/15 text-cream/55',
                editing && 'hover:border-cream/40 cursor-pointer',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <PriceInput label="Ask" value={draft.ask_price} editing={editing} onChange={(v) => setDraft({ ...draft, ask_price: v })} />
        <PriceInput label="Offered" value={draft.offered_price} editing={editing} onChange={(v) => setDraft({ ...draft, offered_price: v })} />
        <PriceInput label="Agreed" value={draft.agreed_price} editing={editing} onChange={(v) => setDraft({ ...draft, agreed_price: v })} tone="success" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Buyer company</Label>
          <Input value={draft.buyer_company ?? ''} disabled={!editing} onChange={(e) => setDraft({ ...draft, buyer_company: e.target.value })} />
        </div>
        <div>
          <Label>Buyer contact</Label>
          <Input value={draft.buyer_contact ?? ''} disabled={!editing} onChange={(e) => setDraft({ ...draft, buyer_contact: e.target.value })} />
        </div>
      </div>

      <div>
        <Label>Negotiation strategy</Label>
        <Textarea
          value={draft.negotiation_strategy ?? ''}
          disabled={!editing}
          placeholder="What's our walk-away price? What concessions are on the table? Who has the leverage and why?"
          onChange={(e) => setDraft({ ...draft, negotiation_strategy: e.target.value })}
        />
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea
          value={draft.notes ?? ''}
          disabled={!editing}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </div>

      <PlaybookCallout stage={draft.stage} />
    </Card>
  )
}

function PriceInput({ label, value, editing, onChange, tone = 'cream' }: {
  label: string
  value: number | undefined
  editing: boolean
  onChange: (v: number | undefined) => void
  tone?: 'cream' | 'success'
}) {
  return (
    <div>
      <Label>{label}</Label>
      {editing ? (
        <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)} />
      ) : (
        <div className={cn('font-display italic text-3xl font-light leading-none py-1', tone === 'success' ? 'text-success' : 'text-cream')}>
          {formatCurrency(value, { compact: true })}
        </div>
      )}
    </div>
  )
}

function PlaybookCallout({ stage }: { stage: AcquisitionStage }) {
  const playbook: Record<AcquisitionStage, { title: string; bullets: string[] }> = {
    'not-listed': { title: 'Not yet listed', bullets: [
      'Decide if this app is genuinely an acquisition target, or just lifestyle infrastructure.',
      'If yes: write the one-paragraph "what they\'re actually buying" pitch.',
      'Generate the marketing deck and tech whitepaper before any outbound.',
    ]},
    'lead': { title: 'Lead — first contact', bullets: [
      'Confirm budget authority. Cold-curious vs. budget-in-hand are different conversations.',
      'Send the marketing deck. Do NOT send code or numbers yet.',
      'Schedule a 30-min discovery call before sharing the whitepaper.',
    ]},
    'qualified': { title: 'Qualified — they have authority + budget', bullets: [
      'Send the tech whitepaper.',
      'Walk through the valuation rationale; anchor high.',
      'Pre-emptively raise the obvious risks (compliance, single-tenant origin, etc.).',
    ]},
    'due-diligence': { title: 'Due Diligence', bullets: [
      'Stand up a private repo fork or reviewer-only Firebase project — no production data.',
      'Provide the audit trail: changelog, deploy log, Firestore rules, security headers.',
      'Add their representative to the due_diligence_checklist below.',
    ]},
    'loi': { title: 'LOI — Letter of Intent', bullets: [
      'If the deal touches an RRM-adjacent asset, confirm the adversary settlement status with counsel before signing.',
      'Negotiate exclusivity narrowly: 30 days max, scoped to this asset only, no related entities.',
      'Lock the asset list precisely (code, domain, social handles, customers, brand).',
    ]},
    'closing': { title: 'Closing', bullets: [
      'Confirm wire-transfer details with two-channel verification (call + email).',
      'Stand up the DBA / asset assignment paperwork before funds move.',
      'Hold a 30-day post-close handoff retainer in escrow as professional grace.',
    ]},
    'closed-won': { title: 'Closed Won 🏁', bullets: [
      'Document the outcome in the changelog.',
      'Apply allocation rules from the Strategy page (tax reserve, family, foundation).',
      'Run a written postmortem within 14 days while it\'s fresh.',
    ]},
    'closed-lost': { title: 'Closed Lost', bullets: [
      'Note the reason on the record. Pricing? Fit? Trust?',
      'Send a graceful closing email — they may come back, or refer.',
      'Move the app back to "not-listed" or queue it for repositioning.',
    ]},
  }
  const p = playbook[stage]
  return (
    <div className="bg-gold/8 border border-gold/30 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase size={14} className="text-gold-soft" />
        <span className="eyebrow !text-gold-soft">Playbook · {p.title}</span>
      </div>
      <ul className="space-y-1.5">
        {p.bullets.map((b, i) => (
          <li key={i} className="font-body text-sm text-cream/80 flex gap-2">
            <span className="text-gold-soft shrink-0">·</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
